import { resolveTenantLegacyApiUrl } from '../../shared/api/resolveApiUrl.js';
import { ApiError } from '../../shared/api/ApiClient.js';
import { isCommerceBlockedErrorCode } from 'pi-kiosk-shared/eet/CommerceBlockedErrorCode';
import { donationErrorMessage } from '../../shared/api/errorCodes.js';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { mapPromoCodeApplyError } from '../promotions/promoCodeApplyErrors.js';

export interface GatewayMethodOption {
  readonly id: string;
  readonly name: string;
  readonly enabled?: boolean;
}

interface GatewayMethodsApiResponse {
  readonly success: boolean;
  readonly data?: {
    readonly methods?: GatewayMethodOption[];
    readonly providerSlotId?: string;
    readonly readinessBlocked?: boolean;
    readonly readinessCode?: string;
  };
  readonly error?: string;
}

export interface GatewayMethodsFetchResult {
  readonly methods: readonly GatewayMethodOption[];
  /** Resolved gateway slot from GetGatewayMethods (FD-34). */
  readonly providerSlotId: string;
  readonly readinessBlocked?: boolean;
  readonly readinessCode?: string;
}

/** True when gateway methods are ready to offer CARD / start payment. */
export function isGatewayMethodsPayable(
  result: GatewayMethodsFetchResult | null | undefined,
): boolean {
  if (result === null || result === undefined) {
    return false;
  }
  if (result.readinessBlocked === true) {
    return false;
  }
  return result.methods.length > 0;
}

export async function fetchGatewayMethods(params: {
  readonly apiBaseUrl: string;
  readonly tenantCode: string;
  readonly salesPointId: number;
  /** Session / charge currency — required for settlement-route BA resolve. */
  readonly currency: string;
  /** Defaults to PRODUCT_PURCHASE; donation journeys pass DONATION. */
  readonly purpose?: 'PRODUCT_PURCHASE' | 'DONATION';
}): Promise<GatewayMethodsFetchResult> {
  const url = resolveTenantLegacyApiUrl(
    params.apiBaseUrl,
    params.tenantCode,
    '/payments/gateway-methods',
  );
  const currency = params.currency.trim().toUpperCase();
  if (currency.length === 0) {
    throw new ApiError(
      resolvePublicApiFailureMessage({
        status: 400,
        code: 'CURRENCY_NOT_ALLOWED',
      }),
      400,
      'CURRENCY_NOT_ALLOWED',
    );
  }
  const query = new URLSearchParams({
    salesPointId: String(params.salesPointId),
    currency,
  });
  if (params.purpose != null) {
    query.set('purpose', params.purpose);
  }
  const response = await stormAwareFetch(`${url}?${query.toString()}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  let json: GatewayMethodsApiResponse;
  try {
    json = (await response.json()) as GatewayMethodsApiResponse;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }
  if (!response.ok || !json.success || !json.data) {
    throw customerEnvelopeApiError(response.status, json);
  }
  const methods = (json.data.methods ?? []).filter(
    (method) => method.enabled !== false && method.id.length > 0,
  );
  const providerSlotId = json.data.providerSlotId?.trim() ?? '';
  if (providerSlotId.length === 0) {
    throw new ApiError(
      resolvePublicApiFailureMessage({
        status: response.status,
        code: 'MISSING_DATA',
      }),
      response.status,
      'MISSING_DATA',
    );
  }
  return {
    methods,
    providerSlotId,
    readinessBlocked: json.data.readinessBlocked,
    readinessCode: json.data.readinessCode,
  };
}

/**
 * Payment-create toast messages. Known promo/entitlement codes reuse apply §20 map
 * (e.g. TENANT_ENTITLEMENT_PROMO_SALES_POINT_NOT_ALLOWED) when `translate` is provided.
 * Never returns raw `error.message` — callers pass `checkout.paymentStartFailed` /
 * `errors.paymentStartFailed` style public fallbacks.
 */
export function resolveCustomerPaymentStartErrorMessage(
  error: unknown,
  fallbackMessage: string,
  translate?: (messageKey: string) => string,
): string {
  if (error instanceof ApiError) {
    // HYG-G15 — commerce fail-closed → generic payment unavailable (never raw code).
    if (isCommerceBlockedErrorCode(error.code)) {
      if (translate != null) {
        return translate('shop.payDisabled.paymentsUnavailable');
      }
      return donationErrorMessage(error.code, fallbackMessage);
    }
    if (error.code === 'TENANT_SWITCH_REDIRECT') {
      return fallbackMessage;
    }
    if (error.code === 'COLLECT_LOCKED_AFTER_PAYMENT_START') {
      return fallbackMessage;
    }
    // Business 409 — terminal/completed session: recovery copy, not generic start-failed.
    if (error.code === 'CHECKOUT_SESSION_INVALID_STATE') {
      if (translate != null) {
        return translate('errors.checkoutSessionInvalidState');
      }
      return donationErrorMessage(error.code, fallbackMessage);
    }
    // Business 409 — map before infra/message hide so customers get recovery copy.
    if (error.code === 'SCHEDULED_PICKUP_NOT_ENABLED') {
      if (translate != null) {
        return translate('errors.scheduledPickupNotEnabled');
      }
      return donationErrorMessage(error.code, fallbackMessage);
    }
    if (translate != null && error.code != null && error.code.length > 0) {
      const mapping = mapPromoCodeApplyError({ code: error.code });
      if (mapping.messageKey !== 'promo.codeEntry.genericError') {
        return translate(mapping.messageKey);
      }
    }
    if (error.code != null && error.code.length > 0) {
      const unmappedSentinel = '__UNMAPPED_PAYMENT_START__';
      const mapped = donationErrorMessage(error.code, unmappedSentinel);
      if (mapped !== unmappedSentinel) {
        return mapped;
      }
    }
  }
  return fallbackMessage;
}
